import { streamingActions } from "modules/streaming";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { convertMapToValues, isDisplayPeriod } from "utility";
import { hasId } from "utility/modelTypeGuards";
import { isShippingItem } from "utility/ticket";

import type { ProductData } from "@spwn/types/firebase/firestore";
import type { Store } from "store";

type CurrentViewType = "goodsList" | "cart" | "goodsInfo" | "confirm";
type LiveCommerceErrorInfo = {
  isError: boolean;
  caption: string;
  msg: string;
};

export const useLiveCommerce = () => {
  const dispatch = useDispatch();

  const [selectType, setSelectType] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<CurrentViewType>("goodsList");

  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const goods = useSelector((state: Store) => state.ticket.eventGoods);
  const eventGoodsMap = useSelector(
    (state: Store) => state.ticket.eventGoodsMap
  );
  const eventTicketMap = useSelector(
    (state: Store) => state.ticket.eventTicketMap
  );
  const { cartItemMap, cartTotalNum, isPurchased, errorInfo } = useSelector(
    (state: Store) => state.streaming.liveCommerce
  );

  const pseudoCart = useMemo(
    () => convertMapToValues(cartItemMap),
    [cartItemMap]
  );

  const selectedLiveCommerceGoods = goods
    .filter(
      (goods) =>
        (isDisplayPeriod(
          goods.display,
          goods.releaseDateTime,
          goods.closeDateTime
        ) &&
          isShippingItem(goods)) ||
        (goods.display && isAdmin)
    )
    .sort(
      (prev, cur) =>
        cur.releaseDateTime.seconds - prev.releaseDateTime.seconds ||
        prev.priority - cur.priority
    );

  const mapEventItem = useCallback(
    (itemData: ProductData): ProductData | undefined => {
      if (!hasId(itemData)) {
        return;
      }
      const { productType } = itemData;
      if (productType === "ticket") {
        return eventTicketMap[itemData._id];
      }

      if (productType === "goods") {
        return eventGoodsMap[itemData._id];
      }
    },
    [eventGoodsMap, eventTicketMap]
  );

  const incrementCartItem = useCallback(
    (itemData: ProductData) => {
      const item = mapEventItem(itemData);
      if (!item) {
        return;
      }
      dispatch(streamingActions.updateLiveCommerceCart({ item, count: 1 }));
    },
    [dispatch, mapEventItem]
  );

  const decrementCartItem = useCallback(
    (itemData: ProductData) => {
      const item = mapEventItem(itemData);
      if (!item) {
        return;
      }

      dispatch(streamingActions.updateLiveCommerceCart({ item, count: -1 }));
    },
    [dispatch, mapEventItem]
  );

  const getCartItem = useCallback(
    (itemId: string) => {
      return pseudoCart.find((e) => e._id === itemId);
    },
    [pseudoCart]
  );

  const getAddItem = useCallback(
    (classGoodsList: ProductData[]) => {
      const addItem =
        classGoodsList.length === 1
          ? classGoodsList[0]
          : selectType
            ? classGoodsList.filter((el) => el._id === selectType)[0]
            : null;
      return addItem;
    },
    [selectType]
  );

  const addToCart = useCallback(
    (classGoodsList: ProductData[]) => {
      const addItem = getAddItem(classGoodsList);
      if (!addItem) {
        return;
      }
      incrementCartItem(addItem);
    },
    [getAddItem, incrementCartItem]
  );

  const addToCartToBuy = useCallback(() => {
    const addItem = getAddItem(selectedLiveCommerceGoods);
    if (!addItem) {
      return;
    }
    // カートに商品がなければ追加する
    if (!hasId(addItem)) {
      return;
    }
    const item = getCartItem(addItem._id);
    if (!item) {
      incrementCartItem(addItem);
    }
    setCurrentView("cart");
  }, [selectedLiveCommerceGoods, getAddItem, getCartItem, incrementCartItem]);

  const removeCartItem = (itemData: ProductData) => {
    const item = mapEventItem(itemData);
    if (!item) {
      return;
    }

    dispatch(
      streamingActions.updateLiveCommerceCart({
        item,
        count: 0,
        isRemove: true,
      })
    );
  };

  return {
    isPurchased,
    pseudoCart,
    cartTotalNum,
    selectedLiveCommerceGoods,
    currentView,
    errorInfo: errorInfo as LiveCommerceErrorInfo,
    setCurrentView,
    setSelectType,
    incrementCartItem,
    decrementCartItem,
    addToCart,
    addToCartToBuy,
    removeCartItem,
  };
};
